import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { subscriptionsAPI } from '../api';

const TIER_LABELS = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  ai_pro: 'AI Pro',
  enterprise: 'Enterprise',
};

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedStore } = useStore();

  const sessionId = searchParams.get('session_id');
  const isDemo   = searchParams.get('demo') === 'true';
  const tier     = searchParams.get('tier');
  const storeId  = searchParams.get('store_id') || selectedStore?.id;

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [activatedTier, setActivatedTier] = useState(tier || null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        const params = {};
        if (isDemo)      { params.demo = true; params.tier = tier; params.store_id = storeId; }
        else if (sessionId) { params.session_id = sessionId; }
        else { setStatus('error'); setErrorMsg('No session found.'); return; }

        await subscriptionsAPI.verifySession(params);

        // If demo, the tier is already in the URL; for real Stripe we read it from sub
        if (!isDemo && selectedStore) {
          try {
            const subRes = await subscriptionsAPI.getStoreSubscription(selectedStore.id);
            setActivatedTier(subRes.data?.subscription?.tier || tier);
          } catch (_) {}
        }
        setStatus('success');
      } catch (e) {
        setStatus('error');
        setErrorMsg(e?.response?.data?.detail || 'Could not verify payment. Please contact support.');
      }
    };
    verify();
  }, [isDemo, selectedStore, sessionId, storeId, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'verifying') {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div className="spinner" style={{ margin: '0 auto 24px' }}></div>
        <h2>Verifying your payment…</h2>
        <p className="text-muted">Please wait while we confirm your subscription.</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <h2 style={{ marginTop: 16 }}>Verification Failed</h2>
        <p className="text-muted" style={{ marginBottom: 24 }}>{errorMsg}</p>
        <button className="btn btn-primary" onClick={() => navigate('/billing')}>
          Go to Billing
        </button>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
      <h1 style={{ color: '#10B981', marginBottom: 8 }}>Payment Successful!</h1>
      {activatedTier && (
        <p style={{ fontSize: '1.1rem', marginBottom: 8 }}>
          Your <strong>{TIER_LABELS[activatedTier] || activatedTier}</strong> plan is now active.
        </p>
      )}
      {isDemo && (
        <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 16 }}>
          Demo mode — no real charge was made.
        </p>
      )}
      <p className="text-muted" style={{ marginBottom: 32 }}>
        You now have full access to all features included in your plan.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/billing')}>
          View Billing
        </button>
      </div>
    </div>
  );
}
